import { StatProps } from 'components/WorkoutCell/types';
import {
  DistanceUnitShort,
  PublicWorkout,
  PublicWorkoutExercise,
  PublicWorkoutExerciseSet,
  WeightUnit,
  workoutDistanceMeters,
  workoutDurationSeconds,
  UserWorkoutExercise,
  UserWorkoutExerciseSet,
  workoutReps,
} from 'hevy-shared';
import { formatNumber, formatNumberWithUnit } from 'translations/formatters';
import { t } from 'translations/t';
import {
  secondsToWordFormatLocalized,
  secondsToWordFormatMinutesLocalized,
} from 'translations/units';
import { userDistance, userShortDistance, userWeight } from 'utils/units';

interface Preferences {
  weightUnit: WeightUnit;
  distanceUnitShort: DistanceUnitShort;
}

export const calculateSecondColumnHeader = (
  e: UserWorkoutExercise | PublicWorkoutExercise,
): string => {
  switch (e.exercise_type) {
    case 'weight_reps':
    case 'bodyweight_assisted_reps':
    case 'bodyweight_reps':
      return t('web.workoutDetails.secondColumnHeader.weightReps');
    case 'reps_only':
      return t('web.workoutDetails.secondColumnHeader.reps');
    case 'distance_duration':
      return t('web.workoutDetails.secondColumnHeader.distanceTime');
    case 'duration':
      return t('web.workoutDetails.secondColumnHeader.duration');
    case 'short_distance_weight':
      return t('web.workoutDetails.secondColumnHeader.weightDistance');
    case 'weight_duration':
      return t('web.workoutDetails.secondColumnHeader.weightDuration');
    case 'floors_duration':
      return t('web.workoutDetails.secondColumnHeader.floorsDuration');
    case 'steps_duration':
      return t('web.workoutDetails.secondColumnHeader.stepsDuration');
  }
};

export const calculateSetValue = (
  e: UserWorkoutExercise | PublicWorkoutExercise,
  s: UserWorkoutExerciseSet | PublicWorkoutExerciseSet,
  p: Preferences,
): string => {
  switch (e.exercise_type) {
    case 'weight_reps':
    case 'bodyweight_assisted_reps':
    case 'bodyweight_reps': {
      const weight = userWeight(s.weight_kg ?? 0, p.weightUnit);
      return `${formatNumberWithUnit(weight, p.weightUnit, {
        maximumFractionDigits: 2,
        unitDisplay: 'narrow',
      })} x ${formatNumberWithUnit(s.reps ?? 0, 'reps')}`;
    }

    case 'reps_only': {
      return formatNumberWithUnit(s.reps ?? 0, 'reps');
    }

    case 'distance_duration': {
      const distance = userDistance(s.distance_meters ?? 0, p.distanceUnitShort);
      const time = secondsToWordFormatLocalized(s.duration_seconds ?? 0);
      return `${formatNumberWithUnit(distance, p.distanceUnitShort, {
        maximumFractionDigits: 1,
        unitDisplay: 'narrow',
      })} - ${time}`;
    }

    case 'duration': {
      return `${secondsToWordFormatLocalized(s.duration_seconds ?? 0)}`;
    }
    case 'short_distance_weight': {
      const weight = userWeight(s.weight_kg ?? 0, p.weightUnit);
      const shortDistance = userShortDistance(s.distance_meters ?? 0);
      const distanceUnits = p.distanceUnitShort === 'km' ? 'm' : 'yd';
      return `${formatNumberWithUnit(weight, p.weightUnit, {
        maximumFractionDigits: 2,
        unitDisplay: 'narrow',
      })} - ${formatNumberWithUnit(shortDistance, distanceUnits, { unitDisplay: 'narrow' })}`;
    }
    case 'weight_duration': {
      const weight = userWeight(s.weight_kg ?? 0, p.weightUnit);
      const duration = `${secondsToWordFormatLocalized(s.duration_seconds ?? 0)}`;
      return `${formatNumberWithUnit(weight, p.weightUnit, {
        maximumFractionDigits: 2,
        unitDisplay: 'narrow',
      })} x ${duration}`;
    }
    case 'floors_duration': {
      const floors = formatNumber(s.custom_metric ?? 0);
      const duration = `${secondsToWordFormatLocalized(s.duration_seconds ?? 0)}`;
      return `${floors} ${t('web.exercise.charts.unit.floors')} - ${duration}`;
    }
    case 'steps_duration': {
      const steps = formatNumber(s.custom_metric ?? 0);
      const duration = `${secondsToWordFormatLocalized(s.duration_seconds ?? 0)}`;
      return `${steps} ${t('web.exercise.charts.unit.steps')} - ${duration}`;
    }
  }
};

export const calculatePublicWorkoutStats = (workout: PublicWorkout): StatProps[] => {
  const { weight_unit, distance_unit } = workout;
  const volumeKg = workout.estimated_volume_kg;
  const durationSeconds = workoutDurationSeconds(workout);
  const reps = workoutReps(workout);
  const distanceMeters = workoutDistanceMeters(workout);

  const stats: StatProps[] = [
    {
      title: t('web.workoutDetails.stats.duration'),
      value: secondsToWordFormatMinutesLocalized(durationSeconds),
      isPr: false,
    },
  ];

  const userVolume = userWeight(volumeKg, weight_unit);
  const distance = userDistance(distanceMeters, distance_unit);

  if (volumeKg > reps) {
    stats.push({
      title: t('web.workoutDetails.stats.volume'),
      value: formatNumberWithUnit(userVolume, weight_unit, { maximumFractionDigits: 0 }),
      isPr: false,
    });
  } else if (reps > distanceMeters) {
    stats.push({ title: t('web.workoutDetails.stats.reps'), value: `${reps}`, isPr: false });
  } else if (distance > 0) {
    stats.push({
      title: t('web.workoutDetails.stats.distance'),
      value: formatNumberWithUnit(distance, distance_unit, { maximumFractionDigits: 2 }),
      isPr: false,
    });
  }

  return stats;
};
